import { useMutation, useQuery } from '@apollo/client';
import { useToast } from '@retainerclub/hooks';
import {
  NOTIFICATION_SETTINGS,
  UPDATE_NOTIFICATION_TYPE_USER,
} from '@retainerclub/shared-api';
import { useState } from 'react';
import { ErrorMessage } from '../../../shared';

function NotificationSettings() {
  const { setToast } = useToast();
  const [mutationErrors, setMutationErrors] = useState([]);
  const [requestError, setRequestError] = useState(null);
  const { data, error, loading, refetch } = useQuery(NOTIFICATION_SETTINGS);
  const [updateNotificationTypeUser, { loading: saving, error: mutationError }] =
    useMutation(UPDATE_NOTIFICATION_TYPE_USER);

  const settings = data?.notificationSettings || [];

  async function toggleNotificationSetting(setting) {
    setMutationErrors([]);
    setRequestError(null);

    try {
      const response = await updateNotificationTypeUser({
        variables: {
          input: {
            input: {
              notificationTypeId: setting.notificationType.id,
              enabled: !setting.enabled,
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      const result = response?.data?.updateNotificationTypeUser;
      const nextMutationErrors = result?.errorMessages || [];

      if (nextMutationErrors.length > 0) {
        setMutationErrors(nextMutationErrors);
        return;
      }

      await refetch();

      setToast({
        show: true,
        title: 'Success',
        message: 'Notification settings updated',
        type: 'success',
      });
    } catch (err) {
      setRequestError(err);
    }
  }

  return (
    <section className="grid grid-cols-1 gap-x-8 gap-y-10 border-black/10 border-b pb-12 md:grid-cols-3">
      <div>
        <h2 className="font-semibold text-base text-black leading-7">
          Notifications
        </h2>
        <p className="mt-1 text-black/70 text-sm leading-6">
          Choose which RetainerClub notifications are sent to your inbox.
        </p>
      </div>

      <div className="max-w-2xl md:col-span-2">
        <div className="space-y-6">
          {(error || mutationError || requestError || mutationErrors.length > 0) && (
            <ErrorMessage
              error={error || mutationError || requestError}
              errorMessages={mutationErrors}
            />
          )}

          {loading ? (
            <div className="text-black/70 text-sm">
              Loading notification settings...
            </div>
          ) : settings.length === 0 ? (
            <div className="text-black/70 text-sm leading-6">
              No notification settings are available for this profile.
            </div>
          ) : (
            <div>
              {settings.map((setting, index) => (
                <label
                  className={`flex items-start justify-between gap-4 py-4 ${
                    index !== settings.length - 1 ? 'border-black/10 border-b' : ''
                  } ${saving ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
                  key={setting.notificationType.id}
                >
                  <span className="pr-4">
                    <span className="block font-medium text-black text-sm leading-6">
                      {setting.notificationType.name}
                    </span>
                    <span className="mt-1 block text-black/70 text-sm leading-6">
                      {setting.notificationType.description}
                    </span>
                  </span>

                  <span className="flex h-6 items-center">
                    <input
                      checked={setting.enabled}
                      className="size-4 rounded border-rcgray-600 text-rcprimary-400 focus:ring-rcprimary-400 disabled:cursor-not-allowed disabled:bg-rcgray-600 disabled:text-rcgray-600 disabled:ring-rcgray-700"
                      disabled={saving}
                      onChange={() => toggleNotificationSetting(setting)}
                      type="checkbox"
                    />
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default NotificationSettings;
